import React from "react";
import { Flex } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { AddressInput } from "../AddressInput";
import { IndividualInformationInput } from "../IndividualInformationInput";
import { createIndividualProfileSchema } from "./schemas";
import { defaultInitialValues, IndividualFormProps, IndividualProfile } from "./types";

/**
 * `IndividualForm` is a presentational component that renders a group of entity fields for capturing individual profile data.
 *
 * This component is purely presentational and does not contain any business logic. It only renders the form fields.
 *
 * The form actions (like form submission) are not handled within this component. Instead, they are managed by the parent
 * component, which can control the form using the `innerRef` prop.
 *
 * @example
 * Here's an example of how to use this component with `useRef` to control the form:
 *
 * ```tsx
 * import { useRef } from 'react';
 * import { FormikProps } from 'formik';
 * import { IndividualForm } from './IndividualForm';
 *
 * const MyComponent = () => {
 *   const formRef = useRef<FormikProps<IndividualProfile>>(null);
 *
 *   const handleSubmit = () => {
 *     if (formRef.current) {
 *       formRef.current.handleSubmit();
 *     }
 *   };
 *
 *   return (
 *     <>
 *       <IndividualForm
 *         initialValues={...} // Your initial values here
 *         onContinue={(values) => console.log("Form submitted", values)}
 *         innerRef={formRef}
 *       />
 *       <button onClick={handleSubmit}>Submit</button>
 *     </>
 *   );
 * };
 * ```
 */
export const IndividualForm: React.FC<IndividualFormProps> = ({
  initialValues,
  isUpdating,
  onBack,
  onContinue,
  profileName,
  showAccountTypeDropdown = true,
}: IndividualFormProps) => {
  const mergedInitialValues = { ...defaultInitialValues, ...initialValues };

  return (
    <>
      <Form
        initialValues={mergedInitialValues}
        onSubmit={async (values: Partial<IndividualProfile>) => {
          await onContinue(values as IndividualProfile);
        }}
        yupValidationSchema={createIndividualProfileSchema}
        submitOnlyChangedValues={false}
      >
        <IndividualInformationInput
          title="Details"
          meta={{ headerType: "h4" }}
          profileName={profileName}
          showAccountTypeDropdown={showAccountTypeDropdown}
        />
        <AddressInput title="Address" meta={{ headerType: "strong-md" }} />
        <Flex justifyContent="flex-end" gap={Sizes.MD}>
          <CancelButton onClick={onBack}></CancelButton>
          <SubmitButton isLoading={isUpdating}>Save and exit </SubmitButton>
        </Flex>
      </Form>
    </>
  );
};
